/**
 * DataTable Component (epic #2730 / issue #2738 · sort: epic #2859 / issue #2861)
 *
 * Generic, type-safe table primitive modeled after the Backend.AI WebUI
 * `BAITable`. Designed so future menus (Models, Sessions, Data, ...)
 * can share one tabular surface instead of inventing a new one each
 * time.
 *
 * Features (V1 — additional capabilities planned for follow-up issues):
 * - Type-safe column definitions with custom cell renderers
 * - Optional column-visibility persistence via `localStorage` when
 *   `persistKey` is supplied
 * - Optional resizable columns (drag the right edge of a header cell)
 *   with widths persisted alongside visibility
 * - Empty-state slot rendered when `rows` is empty
 * - Loading-state slot rendered when `loading` is `true`
 * - Stable `getRowKey` accessor so rows with the same identifier remain
 *   referentially identical across event-driven updates
 *
 * Sorting (V2 — epic #2859 / issue #2861):
 * - Optional per-column sortable flag with sort value accessor or
 *   custom comparator
 * - Optional defaultSortDirection per column
 * - Controlled (`sortColumnId` + `sortDirection` + `onSortChange`) and
 *   uncontrolled (internal state) modes
 * - Accessible `aria-sort` attributes on header cells
 * - Keyboard activation (Enter / Space) for sortable headers
 * - Stable sort — equal-valued rows retain their original order
 * - Sorting never mutates the original `rows` prop
 *
 * The component is intentionally domain-agnostic — it MUST NOT carry
 * Session, Model, or Agent-specific knowledge. The first consumer is
 * `SessionListTable` (epic #2730 / #2738) but the API is generic.
 */

import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import "./DataTable.css";

// ============================================================================
// Public types
// ============================================================================

/** Direction a column is currently sorted. */
export type SortDirection = "asc" | "desc";

export interface DataTableColumn<T> {
  /** Unique identifier for the column. Used for visibility + width persistence. */
  id: string;
  /** Header label. */
  header: ReactNode;
  /**
   * Cell renderer. Receives the row and returns a React node. Pure
   * functions are encouraged so memoization stays effective.
   */
  render: (row: T, index: number) => ReactNode;
  /** Optional minimum column width in pixels. */
  minWidth?: number;
  /**
   * Optional maximum column width in pixels. Values above this limit are
   * clamped for pointer and keyboard resizing. Resizable columns default to
   * 1200 pixels so every separator exposes a finite ARIA maximum. A
   * `noResize` column keeps its existing unbounded width unless it explicitly
   * supplies this property.
   */
  maxWidth?: number;
  /** Optional initial width in pixels (only honored on the first paint). */
  initialWidth?: number;
  /** When `true`, the column cannot be resized. Defaults to `false`. */
  noResize?: boolean;
  /** When `true`, the column cannot be hidden by the user. Defaults to `false`. */
  alwaysVisible?: boolean;
  /** Extra `class` for the column's `<th>` and `<td>` cells. */
  className?: string;
  /**
   * When provided, the value is exposed via the `align` style on the
   * column's cells. Useful for right-aligned numeric columns.
   */
  align?: "left" | "right" | "center";

  // ---- Sorting API (added epic #2859 / issue #2861) -------------------------

  /**
   * When `true`, the column header renders a sort affordance and clicking
   * (or pressing Enter / Space) cycles through ascending → descending →
   * unsorted states. Defaults to `false`.
   */
  sortable?: boolean;
  /**
   * Extracts a primitive comparable value from a row for sorting purposes.
   * Used when the default ascending/descending behavior is sufficient.
   * Mutually exclusive with `sortComparator` — if both are provided,
   * `sortComparator` takes precedence.
   *
   * @example
   * ```tsx
   * sortValueAccessor: (row) => row.name.toLowerCase()
   * ```
   */
  sortValueAccessor?: (row: T) => string | number | boolean | null | undefined;
  /**
   * Custom comparator used for sorting. Receives two rows and the active
   * sort direction. Return a negative number if `a` should come first, a
   * positive number if `b` should come first, or `0` if they are equal.
   * The table applies a stable fallback on `0` to preserve original order.
   *
   * @example
   * ```tsx
   * sortComparator: (a, b, dir) =>
   *   dir === "asc"
   *     ? a.createdAt - b.createdAt
   *     : b.createdAt - a.createdAt
   * ```
   */
  sortComparator?: (a: T, b: T, direction: SortDirection) => number;
  /**
   * The sort direction applied when this column is first activated.
   * Defaults to `"asc"`.
   */
  defaultSortDirection?: SortDirection;
}

export interface DataTablePersistedState {
  /** column id → user-overridden width in pixels. */
  widths: Record<string, number>;
  /** column id → visibility flag. Missing keys count as visible. */
  visibility: Record<string, boolean>;
}

/**
 * The controls a row can contain that are actions in their own right. A click
 * or an Enter on one of these is that control's business, not the row's.
 */
const INTERACTIVE_DESCENDANT_SELECTOR = [
  "a",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  "label",
  "[contenteditable='true']",
  "[role='button']",
  "[role='link']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * Whether an event inside a clickable row came from a control nested in it.
 *
 * A row that is itself clickable and contains an Edit button has two actions
 * competing for one click. Without this, both fire: the button edits and the
 * row also navigates away from the thing just edited. The row is the fallback,
 * so anything more specific wins.
 */
function fromInteractiveDescendant(
  target: EventTarget | null,
  currentTarget: EventTarget,
): boolean {
  if (!(target instanceof Element) || !(currentTarget instanceof Element)) {
    return false;
  }
  const control = target.closest(INTERACTIVE_DESCENDANT_SELECTOR);
  return (
    control !== null && control !== currentTarget && currentTarget.contains(control)
  );
}

export interface DataTableProps<T> {
  /** Column definitions, in display order. */
  columns: DataTableColumn<T>[];
  /** Row data. */
  rows: T[];
  /**
   * Stable identifier accessor — used as the React key for each row.
   * Must be deterministic for the lifetime of the row.
   */
  getRowKey: (row: T, index: number) => string;
  /** Render this node when `rows` is empty. */
  emptyState?: ReactNode;
  /** Render this node when `loading` is `true`. */
  loadingState?: ReactNode;
  /** When `true`, the loading slot replaces the table body. */
  loading?: boolean;
  /**
   * Column widths and visibility, controlled by the caller.
   *
   * This component used to take a `persistKey` and write to `localStorage`
   * itself. Storage is a policy decision that belongs to the consumer: where
   * it goes, under which key, whether it is per user or per workspace, and
   * whether it exists at all in a host that has no `localStorage`. A
   * rendering component that answers those questions on its own cannot be
   * reused by a consumer that answers them differently.
   */
  columnState?: DataTablePersistedState;
  /**
   * Called whenever the user resizes a column or toggles its visibility.
   * Pair it with `columnState` to persist wherever the consumer keeps
   * preferences; omit both to get a table that forgets on unmount.
   */
  onColumnStateChange?: (state: DataTablePersistedState) => void;
  /** Extra class for the table's wrapping element. */
  className?: string;
  /** ARIA label for the table. Defaults to "Data table". */
  ariaLabel?: string;
  /**
   * Produces the accessible name for a resizable column's separator. The
   * default is `Resize <column id> column`; pass a product's translated label
   * when column ids are not suitable for people to hear.
   */
  resizeHandleLabel?: (column: DataTableColumn<T>) => string;
  /**
   * Produces the human-readable current width announced by a resize
   * separator. The default is `<width> pixels`; pass a translated formatter
   * when the host localizes measurements.
   */
  resizeValueText?: (width: number, column: DataTableColumn<T>) => string;
  /** Test ID for testing harnesses. */
  testId?: string;
  /**
   * Callback invoked when a row is clicked. When provided, rows render
   * with a clickable affordance (cursor: pointer + keyboard binding).
   */
  onRowClick?: (row: T) => void;
  /**
   * Narrows `onRowClick` to the rows that can actually act on it, for a table
   * that mixes actionable and informational rows. A row this returns `false`
   * for gets no pointer affordance, no tab stop, and no keyboard binding, so it
   * is not announced as a button it cannot be.
   */
  isRowClickable?: (row: T, index: number) => boolean;
  /**
   * Extra class for one row, for flagging a specific row without forking the
   * table: a transient deep-link highlight, a stale entry, a row being removed.
   * A falsy return adds nothing.
   */
  rowClassName?: (row: T, index: number) => string | undefined | false | null;

  // ---- Sorting props (added epic #2859 / issue #2861) -----------------------

  /**
   * Controlled — the column currently being sorted. Pass `undefined` to
   * indicate no active sort. When this prop is present, the component
   * operates in controlled mode: the caller is responsible for updating
   * the sort state via `onSortChange`.
   */
  sortColumnId?: string;
  /**
   * Controlled — the direction of the active sort. Ignored when
   * `sortColumnId` is `undefined`.
   */
  sortDirection?: SortDirection;
  /**
   * Called when the user activates a sortable column header. In
   * controlled mode the caller must propagate this back as
   * `sortColumnId` / `sortDirection`. In uncontrolled mode this prop is
   * purely observational.
   *
   * @param columnId  The column being sorted, or `undefined` when the
   *                  sort is cleared.
   * @param direction The new direction.
   */
  onSortChange?: (
    columnId: string | undefined,
    direction: SortDirection | undefined,
  ) => void;
}

const EMPTY_COLUMN_STATE: DataTablePersistedState = { widths: {}, visibility: {} };

/** Keyboard increment, in CSS pixels, for a column resize separator. */
const KEYBOARD_RESIZE_STEP = 16;

/** Product-neutral finite ceiling for a column without an explicit maximum. */
const DEFAULT_MAX_COLUMN_WIDTH = 1200;

interface ColumnWidthBounds {
  min: number;
  max: number;
}

function columnWidthBounds<T>(column: DataTableColumn<T>): ColumnWidthBounds {
  const min = Math.max(0, column.minWidth ?? 80);
  const requestedMax = column.maxWidth ?? DEFAULT_MAX_COLUMN_WIDTH;
  const max = Number.isFinite(requestedMax)
    ? Math.max(min, requestedMax)
    : DEFAULT_MAX_COLUMN_WIDTH;
  return { min, max: Math.max(min, max) };
}

function clampColumnWidth<T>(column: DataTableColumn<T>, width: number): number {
  const { min, max } = columnWidthBounds(column);
  return Math.min(max, Math.max(min, Math.round(width)));
}

// ============================================================================
// Sorting helpers
// ============================================================================

/**
 * Stable sort — rows with equal comparison result keep their original
 * relative order. We achieve stability by augmenting each row with its
 * original index and using that as a tie-breaker.
 *
 * The input array is never mutated.
 */
function stableSort<T>(rows: T[], comparator: (a: T, b: T) => number): T[] {
  const indexed = rows.map((row, i) => ({ row, i }));
  indexed.sort((a, b) => {
    const result = comparator(a.row, b.row);
    return result !== 0 ? result : a.i - b.i;
  });
  return indexed.map(({ row }) => row);
}

/**
 * Builds a comparator from a `DataTableColumn` + active sort direction.
 * Falls back to string comparison when neither `sortComparator` nor
 * `sortValueAccessor` is provided on the column.
 */
function buildComparator<T>(
  col: DataTableColumn<T>,
  direction: SortDirection,
): (a: T, b: T) => number {
  const { sortComparator } = col;
  if (sortComparator) {
    return (a, b) => sortComparator(a, b, direction);
  }
  const accessor = col.sortValueAccessor;
  if (accessor) {
    return (a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      // Nullish values sort last regardless of direction.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      let cmp: number;
      if (typeof av === "number" && typeof bv === "number") {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv));
      }
      return direction === "asc" ? cmp : -cmp;
    };
  }
  // No accessor — unsortable in practice (column should not be sortable).
  return () => 0;
}

/**
 * Returns the `aria-sort` attribute value for a column header cell.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-sort
 */
function ariaSortValue(
  col: DataTableColumn<unknown>,
  activeSortColumnId: string | undefined,
  activeSortDirection: SortDirection | undefined,
): "ascending" | "descending" | "none" | undefined {
  if (!col.sortable) return undefined;
  if (col.id === activeSortColumnId) {
    return activeSortDirection === "asc" ? "ascending" : "descending";
  }
  return "none";
}

// Sort indicator icons rendered as inline SVG so there is no external
// asset dependency. They intentionally reuse design-token colours so they
// automatically adapt to light / dark themes.

function SortIcon({ direction }: { direction: SortDirection | "none" }) {
  if (direction === "none") {
    // Neutral chevron stack — both arrows in muted colour
    return (
      <span
        className="data-table__sort-icon data-table__sort-icon--none"
        aria-hidden="true"
      >
        <svg
          width="10"
          height="14"
          viewBox="0 0 10 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M5 1L1 5H9L5 1Z" fill="currentColor" opacity="0.35" />
          <path d="M5 13L9 9H1L5 13Z" fill="currentColor" opacity="0.35" />
        </svg>
      </span>
    );
  }
  if (direction === "asc") {
    return (
      <span
        className="data-table__sort-icon data-table__sort-icon--asc"
        aria-hidden="true"
      >
        <svg
          width="10"
          height="14"
          viewBox="0 0 10 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M5 1L1 5H9L5 1Z" fill="currentColor" />
          <path d="M5 13L9 9H1L5 13Z" fill="currentColor" opacity="0.25" />
        </svg>
      </span>
    );
  }
  return (
    <span
      className="data-table__sort-icon data-table__sort-icon--desc"
      aria-hidden="true"
    >
      <svg
        width="10"
        height="14"
        viewBox="0 0 10 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M5 1L1 5H9L5 1Z" fill="currentColor" opacity="0.25" />
        <path d="M5 13L9 9H1L5 13Z" fill="currentColor" />
      </svg>
    </span>
  );
}

// ============================================================================
// Component
// ============================================================================

function DataTableInner<T>({
  columns,
  rows,
  getRowKey,
  emptyState,
  loadingState,
  loading = false,
  columnState,
  onColumnStateChange,
  className = "",
  ariaLabel = "Data table",
  resizeHandleLabel = (column) => `Resize ${column.id} column`,
  resizeValueText = (width) => `${String(width)} pixels`,
  testId,
  onRowClick,
  isRowClickable,
  rowClassName,
  // Sorting (controlled)
  sortColumnId: controlledSortColumnId,
  sortDirection: controlledSortDirection,
  onSortChange,
}: DataTableProps<T>) {
  // ---- Column state (widths + visibility) ----------------------------------
  //
  // Held internally so a caller that does not care about persistence gets a
  // working table, and re-seeded whenever the caller supplies a new one.
  const [persisted, setPersisted] = useState<DataTablePersistedState>(
    () => columnState ?? EMPTY_COLUMN_STATE,
  );

  useEffect(() => {
    if (columnState) setPersisted(columnState);
  }, [columnState]);

  // ---- Sorting state (uncontrolled fallback) -------------------------------
  //
  // Controlled mode is detected by the presence of `sortColumnId` as a prop
  // (the caller explicitly manages the sort column). Using only this prop as
  // the signal (rather than OR-ing with `sortDirection`) avoids a
  // pathological state where `sortDirection` is set but `sortColumnId` is
  // not, which would enter controlled mode with no sort effect.
  //
  // Note: TypeScript cannot distinguish "prop not passed" from "prop passed
  // as undefined", so we rely on the convention that controlled callers
  // always pass both `sortColumnId` and `sortDirection` together.
  const isControlled =
    controlledSortColumnId !== undefined || controlledSortDirection !== undefined;

  const [internalSortColumnId, setInternalSortColumnId] = useState<string | undefined>(
    undefined,
  );
  const [internalSortDirection, setInternalSortDirection] = useState<
    SortDirection | undefined
  >(undefined);

  const activeSortColumnId = isControlled
    ? controlledSortColumnId
    : internalSortColumnId;
  const activeSortDirection = isControlled
    ? controlledSortDirection
    : internalSortDirection;

  /** Handle a click / keypress on a sortable column header. */
  const handleSortToggle = useCallback(
    (col: DataTableColumn<T>) => {
      if (!col.sortable) return;

      let nextColumnId: string | undefined;
      let nextDirection: SortDirection | undefined;

      if (activeSortColumnId !== col.id) {
        // Activate this column in its default direction.
        nextColumnId = col.id;
        nextDirection = col.defaultSortDirection ?? "asc";
      } else if (activeSortDirection === (col.defaultSortDirection ?? "asc")) {
        // Flip to the opposite direction.
        nextColumnId = col.id;
        nextDirection = activeSortDirection === "asc" ? "desc" : "asc";
      } else {
        // Already in the non-default direction — clear the sort.
        nextColumnId = undefined;
        nextDirection = undefined;
      }

      if (!isControlled) {
        setInternalSortColumnId(nextColumnId);
        setInternalSortDirection(nextDirection);
      }
      onSortChange?.(nextColumnId, nextDirection);
    },
    [activeSortColumnId, activeSortDirection, isControlled, onSortChange],
  );

  // ---- Visibility computation (column always-visible wins) -----------------
  const visibleColumns = useMemo(
    () =>
      columns.filter((col) => {
        if (col.alwaysVisible) return true;
        const flag = persisted.visibility[col.id];
        return flag !== false;
      }),
    [columns, persisted.visibility],
  );

  // ---- Width resolution ----------------------------------------------------
  const resolveWidth = useCallback(
    (col: DataTableColumn<T>): number | undefined => {
      const persistedWidth = persisted.widths[col.id];
      const width =
        typeof persistedWidth === "number" && persistedWidth > 0
          ? persistedWidth
          : col.initialWidth;
      if (width === undefined) return undefined;
      if (col.noResize && col.maxWidth === undefined) {
        return Math.max(width, col.minWidth ?? 0);
      }
      return clampColumnWidth(col, width);
    },
    [persisted.widths],
  );

  // ---- Resize handling -----------------------------------------------------
  const headerRefs = useRef(new Map<string, HTMLTableCellElement>());
  const [renderedWidths, setRenderedWidths] = useState<Record<string, number>>({});

  const updateRenderedWidth = useCallback(
    (column: DataTableColumn<T>, width: number) => {
      const nextWidth = clampColumnWidth(column, width);
      setRenderedWidths((previous) =>
        previous[column.id] === nextWidth
          ? previous
          : { ...previous, [column.id]: nextWidth },
      );
    },
    [],
  );

  const setHeaderRef = useCallback(
    (columnId: string, element: HTMLTableCellElement | null) => {
      if (element) headerRefs.current.set(columnId, element);
      else headerRefs.current.delete(columnId);
    },
    [],
  );

  useLayoutEffect(() => {
    const updateFromElement = (column: DataTableColumn<T>, element: Element) => {
      const nextWidth = element.getBoundingClientRect().width;
      if (nextWidth > 0) updateRenderedWidth(column, nextWidth);
    };

    for (const column of visibleColumns) {
      const header = headerRefs.current.get(column.id);
      if (header) updateFromElement(column, header);
    }

    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const columnId = (entry.target as HTMLElement).dataset.columnId;
        const column = visibleColumns.find((candidate) => candidate.id === columnId);
        if (column) updateFromElement(column, entry.target);
      }
    });
    for (const column of visibleColumns) {
      const header = headerRefs.current.get(column.id);
      if (header) observer.observe(header);
    }
    return () => observer.disconnect();
  }, [updateRenderedWidth, visibleColumns]);

  const resizeRef = useRef<{
    columnId: string;
    startX: number;
    startWidth: number;
    minWidth: number;
    maxWidth: number;
    pointerId: number;
    handle: HTMLDivElement;
  } | null>(null);

  const updateColumnWidth = useCallback(
    (column: DataTableColumn<T>, width: number) => {
      const nextWidth = clampColumnWidth(column, width);
      setPersisted((previous) => ({
        ...previous,
        widths: { ...previous.widths, [column.id]: nextWidth },
      }));
      updateRenderedWidth(column, nextWidth);
    },
    [updateRenderedWidth],
  );

  const clearResize = useCallback((pointerId?: number) => {
    const resize = resizeRef.current;
    if (!resize || (pointerId !== undefined && resize.pointerId !== pointerId)) return;
    // Clear the state before releasing capture: releasePointerCapture can
    // synchronously dispatch lostpointercapture in some browsers.
    resizeRef.current = null;
    if (resize.handle.hasPointerCapture?.(resize.pointerId)) {
      resize.handle.releasePointerCapture?.(resize.pointerId);
    }
  }, []);

  useEffect(() => clearResize, [clearResize]);

  const measuredWidth = useCallback(
    (handle: HTMLDivElement, col: DataTableColumn<T>): number => {
      const renderedWidth = handle.parentElement?.getBoundingClientRect().width ?? 0;
      const fallbackWidth =
        renderedWidths[col.id] ?? resolveWidth(col) ?? columnWidthBounds(col).min;
      return clampColumnWidth(col, Math.round(renderedWidth) || fallbackWidth);
    },
    [renderedWidths, resolveWidth],
  );

  const beginResize = useCallback(
    (ev: ReactPointerEvent<HTMLDivElement>, col: DataTableColumn<T>) => {
      if (col.noResize || !ev.isPrimary || ev.button !== 0 || resizeRef.current) {
        return;
      }
      ev.preventDefault();
      ev.stopPropagation();
      const handle = ev.currentTarget;
      resizeRef.current = {
        columnId: col.id,
        startX: ev.clientX,
        startWidth: measuredWidth(handle, col),
        minWidth: columnWidthBounds(col).min,
        maxWidth: columnWidthBounds(col).max,
        pointerId: ev.pointerId,
        handle,
      };
      handle.setPointerCapture?.(ev.pointerId);
    },
    [measuredWidth],
  );

  const moveResize = useCallback(
    (ev: ReactPointerEvent<HTMLDivElement>) => {
      const resize = resizeRef.current;
      if (!resize || resize.pointerId !== ev.pointerId) return;
      ev.preventDefault();
      ev.stopPropagation();
      const nextWidth = Math.max(
        resize.minWidth,
        Math.min(resize.maxWidth, resize.startWidth + ev.clientX - resize.startX),
      );
      const column = columns.find((candidate) => candidate.id === resize.columnId);
      if (column) updateColumnWidth(column, nextWidth);
    },
    [columns, updateColumnWidth],
  );

  const endResize = useCallback(
    (ev: ReactPointerEvent<HTMLDivElement>) => {
      if (resizeRef.current?.pointerId !== ev.pointerId) return;
      ev.preventDefault();
      ev.stopPropagation();
      clearResize(ev.pointerId);
    },
    [clearResize],
  );

  const handleResizeKeyDown = useCallback(
    (ev: ReactKeyboardEvent<HTMLDivElement>, col: DataTableColumn<T>) => {
      if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight" && ev.key !== "Home") {
        return;
      }
      ev.preventDefault();
      ev.stopPropagation();
      const { min: minimum, max: maximum } = columnWidthBounds(col);
      const current = measuredWidth(ev.currentTarget, col);
      const nextWidth =
        ev.key === "Home"
          ? minimum
          : Math.min(
              maximum,
              Math.max(
                minimum,
                current +
                  (ev.key === "ArrowRight"
                    ? KEYBOARD_RESIZE_STEP
                    : -KEYBOARD_RESIZE_STEP),
              ),
            );
      updateColumnWidth(col, nextWidth);
    },
    [measuredWidth, updateColumnWidth],
  );

  // Report changes so the caller can persist them. Skipped while `persisted`
  // still holds what the caller last handed in, so echoing the callback back
  // through `columnState` does not loop.
  const reported = useRef(persisted);
  useEffect(() => {
    if (reported.current === persisted) return;
    reported.current = persisted;
    onColumnStateChange?.(persisted);
  }, [persisted, onColumnStateChange]);

  // ---- Sorted rows --------------------------------------------------------
  const sortedRows = useMemo(() => {
    if (!activeSortColumnId || !activeSortDirection) return rows;
    const col = columns.find((c) => c.id === activeSortColumnId);
    if (!col?.sortable) return rows;
    return stableSort(rows, buildComparator(col, activeSortDirection));
  }, [rows, columns, activeSortColumnId, activeSortDirection]);

  // ---- Body rendering ------------------------------------------------------
  const composedClassName = ["data-table", className].filter(Boolean).join(" ");

  const renderBody = () => {
    if (loading) {
      return (
        <tr className="data-table__row data-table__row--state">
          <td colSpan={visibleColumns.length || 1} className="data-table__state-cell">
            {loadingState ?? <div className="data-table__loading" />}
          </td>
        </tr>
      );
    }
    if (sortedRows.length === 0) {
      return (
        <tr className="data-table__row data-table__row--state">
          <td colSpan={visibleColumns.length || 1} className="data-table__state-cell">
            {emptyState}
          </td>
        </tr>
      );
    }
    return sortedRows.map((row, index) => {
      const key = getRowKey(row, index);
      // A row is interactive only if the table has a handler AND this row is
      // not excluded, so the affordance, the tab stop and the role move
      // together. Splitting them would announce a button that does nothing.
      const clickable = Boolean(onRowClick && (isRowClickable?.(row, index) ?? true));
      const extraClass = rowClassName?.(row, index);
      return (
        <tr
          key={key}
          className={[
            "data-table__row",
            clickable && "data-table__row--clickable",
            extraClass,
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={
            clickable
              ? (e) => {
                  if (fromInteractiveDescendant(e.target, e.currentTarget)) return;
                  onRowClick?.(row);
                }
              : undefined
          }
          onKeyDown={
            clickable
              ? (e) => {
                  if (e.key !== "Enter" && e.key !== " ") return;
                  if (fromInteractiveDescendant(e.target, e.currentTarget)) return;
                  e.preventDefault();
                  onRowClick?.(row);
                }
              : undefined
          }
          tabIndex={clickable ? 0 : undefined}
          role={clickable ? "button" : undefined}
        >
          {visibleColumns.map((col) => (
            <td
              key={col.id}
              className={[
                "data-table__cell",
                col.className,
                col.align && `data-table__cell--align-${col.align}`,
              ]
                .filter(Boolean)
                .join(" ")}
              style={{
                minWidth: col.minWidth,
                maxWidth: col.noResize ? col.maxWidth : columnWidthBounds(col).max,
              }}
            >
              {col.render(row, index)}
            </td>
          ))}
        </tr>
      );
    });
  };

  return (
    <div className={composedClassName} data-testid={testId}>
      <table className="data-table__table" aria-label={ariaLabel} role="table">
        <thead className="data-table__head">
          <tr className="data-table__row data-table__row--head">
            {visibleColumns.map((col) => {
              const width = resolveWidth(col);
              const currentWidth = clampColumnWidth(
                col,
                renderedWidths[col.id] ?? width ?? columnWidthBounds(col).min,
              );
              const bounds = columnWidthBounds(col);
              const isSortActive = col.sortable && col.id === activeSortColumnId;

              return (
                <th
                  key={col.id}
                  ref={(element) => setHeaderRef(col.id, element)}
                  data-column-id={col.id}
                  scope="col"
                  className={[
                    "data-table__cell",
                    "data-table__cell--head",
                    !col.noResize && "data-table__cell--resizable",
                    col.sortable && "data-table__cell--sortable",
                    isSortActive && "data-table__cell--sort-active",
                    col.className,
                    col.align && `data-table__cell--align-${col.align}`,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{
                    width: width === undefined ? undefined : `${String(width)}px`,
                    minWidth: col.minWidth,
                    maxWidth: col.noResize ? col.maxWidth : bounds.max,
                  }}
                  aria-sort={ariaSortValue(
                    col as DataTableColumn<unknown>,
                    activeSortColumnId,
                    activeSortDirection,
                  )}
                  onClick={
                    col.sortable
                      ? () => {
                          handleSortToggle(col);
                        }
                      : undefined
                  }
                  onKeyDown={
                    col.sortable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSortToggle(col);
                          }
                        }
                      : undefined
                  }
                  tabIndex={col.sortable ? 0 : undefined}
                >
                  <span className="data-table__head-label">{col.header}</span>
                  {col.sortable && (
                    <SortIcon
                      direction={
                        isSortActive && activeSortDirection
                          ? activeSortDirection
                          : "none"
                      }
                    />
                  )}
                  {!col.noResize && (
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={resizeHandleLabel(col)}
                      aria-valuemin={bounds.min}
                      aria-valuemax={bounds.max}
                      aria-valuenow={currentWidth}
                      aria-valuetext={resizeValueText(currentWidth, col)}
                      tabIndex={0}
                      className="data-table__resize-handle"
                      onClick={(event) => event.stopPropagation()}
                      onPointerDown={(event) => beginResize(event, col)}
                      onPointerMove={moveResize}
                      onPointerUp={endResize}
                      onPointerCancel={endResize}
                      onLostPointerCapture={(event) => clearResize(event.pointerId)}
                      onKeyDown={(event) => handleResizeKeyDown(event, col)}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="data-table__body">{renderBody()}</tbody>
      </table>
    </div>
  );
}

// Memoize while preserving the generic signature.
export const DataTable = memo(DataTableInner) as typeof DataTableInner;

export default DataTable;
